import { format, isBefore, startOfDay } from 'date-fns';
import { Commitment } from './CommitmentCalendar';
import { Button } from '@/components/ui/button';
import { ReactComponent as NoCommitmentsIcon } from '@/assets/icons/no-commitment.svg';
import { ReactComponent as CommitmentIcon } from '@/assets/icons/commitment-primary.svg';
import { ChevronRightIcon, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PAGE } from '@/routes';
import { Budget } from '@/services/types/budgets.types';
import { cn } from '@/lib/utils';

interface CommitmentListProps {
  date: Date;
  commitments: Commitment[];
  onCreateCommitment?: () => void;
  variant?: 'default' | 'secondary';
  loading?: boolean;
  /** When true, list is shown at md+ (e.g. embedded in budget detail). Default hides on desktop. */
  showOnDesktop?: boolean;
  /** Optional navigation state when opening invoice detail (e.g. budget context). */
  invoiceNavigateState?: {
    budget?: Budget | null;
    from?: 'commitment' | 'budget';
    budgetId?: string;
  };
  className?: string;
}

const CommitmentList = ({
  date,
  commitments,
  onCreateCommitment,
  variant = 'default',
  loading = false,
  showOnDesktop = false,
  invoiceNavigateState,
  className = ''
}: CommitmentListProps) => {
  const hasCommitments = commitments.length > 0;
  const isPastSelectedDate = isBefore(startOfDay(date), startOfDay(new Date()));
  const navigate = useNavigate();
  const handleCommitmentClick = (id: string) => {
    const base = {
      selectedDate: date.toISOString()
    };
    if (invoiceNavigateState) {
      navigate(`${PAGE.INVOICE.path}/${id}`, {
        state: {
          ...base,
          budget: invoiceNavigateState.budget ?? null,
          from: invoiceNavigateState.from ?? 'budget',
          ...(invoiceNavigateState.budgetId ? { budgetId: invoiceNavigateState.budgetId } : {})
        }
      });
    } else {
      navigate(`${PAGE.INVOICE.path}/${id}`, {
        state: {
          ...base,
          budget: null,
          from: 'commitment'
        }
      });
    }
  };

  return (
    <section
      className={cn(
        'bg-white border-t-2 p-4',
        !showOnDesktop && 'md:hidden',
        showOnDesktop && 'md:border-t-0',
        className
      )}
    >
      <header>
        <p className="font-medium text-secondary text-xs">{format(date, 'MMM d')}</p>
      </header>

      {loading ? (
        <div className="flex flex-col items-center justify-center my-8 py-2">
          <Loader2 className="w-5 h-5 animate-spin text-secondary" />
          <p className="text-sm font-medium text-secondary">Loading commitments...</p>
        </div>
      ) : hasCommitments ? (
        <div className="flex flex-col py-2">
          {commitments.map((commitment) => (
            <button
              onClick={() => handleCommitmentClick(commitment.id)}
              key={commitment.id}
              className="flex items-center justify-between gap-3"
            >
              <div className="flex items-center gap-3 py-2.5">
                <div className="bg-lightPurple h-10 w-10 flex items-center justify-center rounded-full p-2">
                  <CommitmentIcon className="w-5 h-5 text-primary" />
                </div>
                <div className="flex flex-col items-start gap-[1px] min-w-0">
                  <span className="font-semibold text-sm truncate">{commitment.title}</span>
                  {commitment.budgetName && (
                    <span className="text-xs text-muted-foreground truncate">{commitment.budgetName}</span>
                  )}
                </div>
              </div>
              {variant === 'secondary' && (
                <button className="h-7 w-7 flex items-center justify-center rounded-full bg-lightPurple-200">
                  <ChevronRightIcon strokeWidth={3} className="w-4 h-4" />
                </button>
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="mt-3 flex flex-col md:flex-row items-center justify-center bg-lightPurple md:bg-transparent rounded-[16px] md:rounded-none gap-2 py-10 px-6 md:p-0">
          <div className="flex items-center gap-3">
            <NoCommitmentsIcon className="w-10 h-10" />
            <div>
              <p className="text-sm font-semibold">No Commitment on this day</p>
              <p className="text-xs text-secondary font-medium">Create a new commitment to make thing on time!</p>
            </div>
          </div>
          {!isPastSelectedDate && onCreateCommitment && (
            <Button
              variant="primary-reverse"
              onClick={onCreateCommitment}
              className="md:hidden rounded-full flex items-center font-semibold text-sm gap-1.5 px-6 mt-2"
            >
              <span>Create a Commitment</span>
            </Button>
          )}
          {!isPastSelectedDate && onCreateCommitment && (
            <Button
              variant="primary-reverse"
              onClick={onCreateCommitment}
              className="hidden md:flex rounded-full items-center justify-center h-8 w-8"
            >
              <span className="text-2xl">+</span>
            </Button>
          )}
        </div>
      )}
    </section>
  );
};

export default CommitmentList;
